/**
 * Environment Configuration
 */

interface EnvironmentConfig {
    // Syncfusion
    syncfusionLicenseKey: string;
  syncfusionServiceUrl?: string;
    
    // Security
    allowedOrigins: string[];
    enabledComponents: Array<'textEditor' | 'spreadsheet' | 'gantt' | 'workflow' | 'runInfo' | 'runAnalysis' | 'graphViewer'>;
    
    // API
    apiBaseUrl: string;
    ilaApiBaseUrl: string;
    
    // Angular App URL (for cross-app navigation)
    ilaBaseUrl: string;
    
    // Environment
    environment: 'development' | 'staging' | 'production';
    isDevelopment: boolean;
    isProduction: boolean;
    
    // Logging
    enableLogging: boolean;
    logLevel: 'debug' | 'info' | 'warn' | 'error';
    
    
    // Performance
    maxFileSizeMB: number;
  }
  
  class Environment {
    private config: EnvironmentConfig;
  
    constructor() {
      this.config = this.loadConfig();
      this.validateConfig();
    }
  
    private loadConfig(): EnvironmentConfig {
      const env = import.meta.env.VITE_ENVIRONMENT || 'development';
  
      return {
        // Syncfusion
        syncfusionLicenseKey: import.meta.env.VITE_SYNCFUSION_LICENSE_KEY || '',
        syncfusionServiceUrl: import.meta.env.VITE_SYNCFUSION_SERVICE_URL || '',
  
        // Security - Allow same origin for standalone deployment
        allowedOrigins: this.parseAllowedOrigins(
          import.meta.env.VITE_ALLOWED_ORIGINS || window.location.origin
        ),
        enabledComponents: this.parseEnabledComponents(
          import.meta.env.VITE_ENABLED_COMPONENTS || 'textEditor,spreadsheet,gantt,workflow,runInfo,runAnalysis,graphViewer'
        ),
  
        // API
        apiBaseUrl: import.meta.env.VITE_API_BASE_URL || 'http://localhost:3000',
        ilaApiBaseUrl: import.meta.env.VITE_ILA_API_BASE_URL || 'http://localhost:8610',
        
        // Angular App URL (for cross-app navigation)
        ilaBaseUrl: import.meta.env.VITE_ILA_BASE_URL ,
        
        // Environment
        environment: env as 'development' | 'staging' | 'production',
        isDevelopment: env === 'development',
        isProduction: env === 'production',
  
        // Logging
        enableLogging: import.meta.env.VITE_ENABLE_LOGGING === 'true',
        logLevel: (import.meta.env.VITE_LOG_LEVEL || 'info') as 'debug' | 'info' | 'warn' | 'error',

        maxFileSizeMB: parseInt(import.meta.env.VITE_MAX_FILE_SIZE_MB || '50', 10),
      };
    }
  
    private parseAllowedOrigins(originsString: string): string[] {
      return originsString
        .split(',')
        .map((origin) => origin.trim())
        .filter((origin) => origin.length > 0);
    }
  
    private validateConfig(): void {
      const errors: string[] = [];
  
      // Validate Syncfusion license key in production
      if (this.config.isProduction && !this.config.syncfusionLicenseKey) {
        errors.push('Syncfusion license key is required in production');
      }
  
      // Validate allowed origins
      if (this.config.allowedOrigins.length === 0) {
        errors.push('At least one allowed origin must be configured');
      }
      if (this.config.enabledComponents.length === 0) {
        errors.push('At least one Syncfusion component must be enabled');
      }
  
      // Validate origins format
      this.config.allowedOrigins.forEach((origin) => {
        try {
          new URL(origin);
        } catch {
          errors.push(`Invalid origin format: ${origin}`);
        }
      });
  
      // Validate numeric values
      if (this.config.maxFileSizeMB <= 0) {
        errors.push('Max file size must be greater than 0');
      }
  
      if (errors.length > 0) {
        const errorMessage = `Environment configuration errors:\n${errors.join('\n')}`;
        console.error(errorMessage);
        
        if (this.config.isProduction) {
          throw new Error(errorMessage);
        }
      }
    }
  
    public get(): EnvironmentConfig {
      return { ...this.config };
    }
  
    public isOriginAllowed(origin: string): boolean {
      return this.config.allowedOrigins.includes(origin);
    }
  
    public getSyncfusionLicenseKey(): string {
      return this.config.syncfusionLicenseKey;
    }
  
    public getIlaBaseUrl(): string {
      return this.config.ilaBaseUrl;
    }
    
    public getApiBaseUrl(): string {
      return this.config.apiBaseUrl;
    }

    public getIlaApiBaseUrl(): string {
      return this.config.ilaApiBaseUrl;
    }
  
    public isFeatureEnabled(feature: 'trackChanges' | 'comments' | 'spellCheck'): boolean {
      switch (feature) {
        default:
          return false;
      }
    }
  
    public shouldLog(level: 'debug' | 'info' | 'warn' | 'error'): boolean {
      if (!this.config.enableLogging) {
        return false;
      }
  
      const levels = ['debug', 'info', 'warn', 'error'];
      const configLevelIndex = levels.indexOf(this.config.logLevel);
      const requestedLevelIndex = levels.indexOf(level);
  
      return requestedLevelIndex >= configLevelIndex;
    }
    
    public getSyncfusionServiceUrl(): string {
      const configured = (this.config.syncfusionServiceUrl || '').trim();
      if (configured) {
        return configured.endsWith('/') ? configured : `${configured}/`;
      }

      // Development fallback: target Syncfusion backend directly to avoid
      // Vite proxy mismatches with ila-api routes.
      if (this.config.isDevelopment) {
        return 'http://localhost:4260/api/documenteditor/';
      }

      // Production fallback: same-origin deployment.
      return '/api/documenteditor/';
    }

    public getEnabledComponents(): Array<'textEditor' | 'spreadsheet' | 'gantt' | 'workflow' | 'runInfo' | 'runAnalysis' | 'graphViewer'> {
      return this.config.enabledComponents;
    }

    public resolveParentOrigin(): string {
      const fallbackOrigin = this.config.allowedOrigins[0] || window.location.origin;

      if (!document.referrer) {
        return fallbackOrigin;
      }

      try {
        const referrerOrigin = new URL(document.referrer).origin;
        if (this.isOriginAllowed(referrerOrigin)) {
          return referrerOrigin;
        }
      } catch {
        // Fall through to configured fallback origin.
      }

      return fallbackOrigin;
    }

    private parseEnabledComponents(
      componentsString: string
    ): Array<'textEditor' | 'spreadsheet' | 'gantt' | 'workflow' | 'runInfo' | 'runAnalysis' | 'graphViewer'> {
      const validComponents = new Set(['textEditor', 'spreadsheet', 'gantt', 'workflow', 'runInfo', 'runAnalysis', 'graphViewer']);
      const parsed = componentsString
        .split(',')
        .map((component) => component.trim())
        .filter((component) => validComponents.has(component));

      return parsed as Array<'textEditor' | 'spreadsheet' | 'gantt' | 'workflow' | 'runInfo' | 'runAnalysis' | 'graphViewer'>;
    }

  }
  
  export const environment = new Environment();
  
  // Export config for direct access
  export const config = environment.get();